import { useEffect, useState } from 'react';

import {
  LookupAutocompleteView,
  mapDropdownOptions,
} from '@/shared/ui/molecules/LookupAutocomplete';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';
import { useDisciplineOptions } from '../../../hooks/useDisciplineOptions';

export interface DisciplineAutocompleteProps {
  /** ID da disciplina selecionada; `null` quando nada. */
  readonly value: number | null;
  /** Rótulo da seleção corrente (exibição inicial). */
  readonly selectedLabel?: string;
  /** Notifica o novo ID e rótulo (`null`/`''` ao limpar). */
  readonly onChange: (id: number | null, label: string) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de disciplina do filtro do Dashboard de Correção.
 * Envolve o `LookupAutocompleteView` compartilhado, que encapsula as armadilhas
 * do `Autocomplete` do ADS — em especial o reset do texto exibido ao limpar.
 *
 * @example
 * ```tsx
 * <DisciplineAutocomplete
 *   value={disciplineId}
 *   selectedLabel={disciplineName}
 *   onChange={(id, label) => { setDisciplineId(id); setDisciplineName(label); }}
 *   label={t('dashboard.filters.discipline')}
 *   placeholder={t('dashboard.filters.disciplinePlaceholder')}
 * />
 * ```
 */
export function DisciplineAutocomplete({
  value,
  selectedLabel: selectedLabelProp = '',
  onChange,
  label,
  placeholder,
}: DisciplineAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState(selectedLabelProp);
  const { options, isQuerySuccess } = useDisciplineOptions(search);

  // Sincroniza o rótulo exibido quando o pai o altera após a montagem
  // (ex.: "Limpar filtros" zerando a seleção).
  useEffect(() => {
    setInputText(selectedLabelProp);
  }, [selectedLabelProp]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value !== null ? String(value) : ''}
      selectedLabel={inputText}
      options={mapDropdownOptions(unwrapPagedResponse(options))}
      isQuerySuccess={isQuerySuccess}
      search={search}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id ? Number(id) : null, itemLabel);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange(null, '');
        setInputText('');
        setSearch('');
      }}
    />
  );
}
